import React, { useState } from 'react'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import { useFormContext } from 'react-hook-form'
import { Grid, Stack, Typography, Button } from '@mui/material_modern'
import { useDispatch, useSelector } from 'react-redux'
import { request } from 'customRequest'
import printJS from 'print-js'

import { useGetOrgImageQuery } from 'services/assets'

import CustomCheckTemplateContainer from 'components/layouts/settings/components/CustomCheckTemplateContainer'
import CheckTemplate from 'components/generic/printable/CheckTemplate'
import PrintDialog from 'components/generic/printable/PrintDialog'

import { defaultSettings } from 'components/containers/settings/utils/defaultSettings'
import { PRINT_DIALOG_TYPES, PDF_TYPE } from 'utils/constants'
import CurrencyToWords from 'utils/data-manipulation/currencyToWords'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader.js'
import useQzTray from 'utils/custom-hooks/useQzTray'
import { dispatchSnackbar } from 'utils/helpers'

const SettingsCustomCheckTemplates = ({ title, subTitle, subTitle2 }) => {
  const { setValue, watch } = useFormContext()
  const watchCheckLayout = watch('purchaseSettings.payment.checkLayout')
  const watchCheckSignatureImage = watch('purchaseSettings.payment.checkSignatureImage')
  const { enableFePdfs } = useSelector(state => state.featureFlags?.variables || {})

  const [openPrintCheckPreview, setOpenPrintCheckPreview] = useState(false)

  const { checkLayout: defaultCheckLayout } = defaultSettings?.purchaseSettings?.payment || {}

  const { data: checkSignatureImageSrc } = useGetOrgImageQuery(
    { filename: watchCheckSignatureImage, isTemp: false },
    { skip: !watchCheckSignatureImage },
  )
  const { qzPrint } = useQzTray()
  const dispatch = useDispatch()

  const [isFetchingPdf, setIsFetchingPdf] = useState(false)

  const pdfPrint = (pdfUrl, pdfTitle, documentType = PDF_TYPE.IBT_SCALE_TICKET) => {
    setIsFetchingPdf(true)
    request(pdfUrl, 'post', { checkLayout: watchCheckLayout }, { accept: 'application/pdf' }, true)
      .then(async response => {
        // Get appropriate printer based on document type
        const printerKeyMap = {
          [PDF_TYPE.IBT_SCALE_TICKET]: 'ticketPrinter',
          [PDF_TYPE.BILL_SETTLEMENT_DOC]: 'documentPrinter',
          [PDF_TYPE.CHECK]: 'checkPrinter',
        }
        const selectedPrinter = localStorage.getItem(printerKeyMap[documentType])

        if (!selectedPrinter || selectedPrinter === 'none') {
          // Use default printJS if no printer is selected
          printJS({
            printable: response,
            type: 'pdf',
            base64: true,
            documentTitle: pdfTitle,
          })
        } else {
          try {
            if (documentType === PDF_TYPE.CHECK) {
              // Checks always print once
              await qzPrint(response, selectedPrinter, 1)
            } else {
              // Other documents use regular qzPrint with copies
              await qzPrint(response, selectedPrinter)
            }
          } catch (error) {
            // Fallback to printJS if QZ fails
            printJS({
              printable: response,
              type: 'pdf',
              base64: true,
              documentTitle: pdfTitle,
            })
          }
        }
        setIsFetchingPdf(false)
      })
      .catch(() => {
        setIsFetchingPdf(false)
        dispatchSnackbar(dispatch, 'error', 'An error occurred while generating the PDF document')
      })
  }
  useGlobalLoader(isFetchingPdf)

  return (
    <>
      <Typography variant="h6" color="textPrimary">
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subTitle}
      </Typography>
      {subTitle2 && (
        <Typography variant="body2" color="textSecondary">
          {subTitle2}
        </Typography>
      )}
      <Grid mt={3}>
        <DndProvider backend={HTML5Backend}>
          <CustomCheckTemplateContainer />
        </DndProvider>
      </Grid>
      <Stack mt={1} direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={() => {
            if (enableFePdfs) {
              pdfPrint(`/core-api/purchases/payments/sample/pdf/${PDF_TYPE.CHECK}`, '', PDF_TYPE.CHECK)
            } else {
              setOpenPrintCheckPreview(true)
            }
          }}
        >
          Print Sample Check
        </Button>
        {!isEqual(watchCheckLayout, defaultCheckLayout) && (
          <Button
            variant="outlined"
            onClick={() => {
              setValue('purchaseSettings.payment.checkLayout', defaultCheckLayout, {
                shouldDirty: true,
              })
            }}
          >
            Reset to default
          </Button>
        )}
      </Stack>
      {openPrintCheckPreview && (
        <PrintDialog
          title="Print Check Info to Template"
          open={openPrintCheckPreview}
          onClose={() => setOpenPrintCheckPreview(false)}
          dialogId="print-check-dialog"
          shouldPrintTitle={false}
          dialogType={PRINT_DIALOG_TYPES.CHECK}
        >
          <CheckTemplate
            checkRecipient="{Check Recipient}"
            customerAddress={{
              address1: '{Address Line 1}',
              address2: '{Address Line 2}',
              city: '{City}',
              state: '{State}',
              zip: '{Zip}',
            }}
            textAmount={CurrencyToWords(0)}
            paymentDate={dateFormatter(new Date())}
            amount="0.00"
            memo="{Memo}"
            checkSignatureImageSrc={checkSignatureImageSrc}
            checkLayoutOverride={watchCheckLayout}
          />
        </PrintDialog>
      )}
    </>
  )
}

SettingsCustomCheckTemplates.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  subTitle2: PropTypes.string,
}

SettingsCustomCheckTemplates.defaultProps = {
  subTitle2: '',
}

export default SettingsCustomCheckTemplates
